/**
 * Single source of truth for CANDIDATE auth redirects.
 *
 * Pure module — no DOM/Node access at module scope, so it is safe to import
 * from edge middleware as well as client components. The few functions that
 * touch sessionStorage guard on `typeof window`.
 *
 * Why this exists: "where does a candidate go after logging in" used to be
 * encoded four different, mutually-inconsistent ways:
 *   - `?redirect=`                 read by /auth/login + /auth/verify
 *   - `?returnUrl=`                set by middleware + dashboard + onboarding,
 *                                  but read by NOBODY  → silently dropped
 *   - `vhire_redirect`             sessionStorage, set by /auth/login (+ dead
 *                                  ProtectedRoute), read by /auth/login + verify
 *   - `vhire_redirect_after_login` sessionStorage, set by api.ts on 401,
 *                                  read by NOBODY      → silently dropped
 *
 * Two of those were dead writes, so a candidate bounced to login (session
 * expiry, or an unauth hit on /me/*) never returned to where they were — they
 * always landed on the dashboard. Worse, the api.ts 401 path pointed at
 * `/login`, which is the EMPLOYER login, not the candidate one.
 *
 * This module collapses everything to ONE query param (`redirect`) + ONE
 * storage key (`vhire_redirect`), and one canonical login path. `returnUrl` is
 * still accepted on READ as a legacy alias so older links keep working.
 */

/**
 * Canonical candidate login route.
 *
 * IMPORTANT: this is NOT `/login` — that path renders the *employer* login
 * (EmployerLoginPage, posts to /tenant-admin/magic-link). Candidates live here.
 */
export const CANDIDATE_LOGIN_PATH = '/auth/login';

/** Canonical query param carrying the post-login destination. */
export const REDIRECT_PARAM = 'redirect';

/** Legacy query param — accepted on read, never written. */
const LEGACY_REDIRECT_PARAM = 'returnUrl';

/** sessionStorage key for the same-tab login round-trip. */
const REDIRECT_STORAGE_KEY = 'vhire_redirect';

/**
 * A redirect target is safe only if it is a same-origin absolute PATH.
 * Rejects empty, protocol-relative (`//evil.com`), and absolute URLs
 * (`https://evil.com`) to close the open-redirect hole.
 */
export function isSafeRedirect(path: string | null | undefined): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

/**
 * Build the candidate login URL, carrying an optional safe return path and an
 * optional `expired` flag (so the login page can explain why the user landed
 * there after a session timeout).
 */
export function buildCandidateLoginUrl(
  opts: { redirect?: string | null; expired?: boolean } = {},
): string {
  const params = new URLSearchParams();
  if (isSafeRedirect(opts.redirect)) params.set(REDIRECT_PARAM, opts.redirect);
  if (opts.expired) params.set('expired', '1');
  const qs = params.toString();
  return qs ? `${CANDIDATE_LOGIN_PATH}?${qs}` : CANDIDATE_LOGIN_PATH;
}

/** Persist a return path for the same-tab login round-trip. No-op if unsafe. */
export function storeRedirect(path: string | null | undefined): void {
  if (typeof window === 'undefined' || !isSafeRedirect(path)) return;
  try {
    window.sessionStorage.setItem(REDIRECT_STORAGE_KEY, path);
  } catch {
    /* sessionStorage can throw in private mode / when disabled — ignore */
  }
}

/** Read the stored return path WITHOUT clearing it. */
export function peekStoredRedirect(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    return isSafeRedirect(v) ? v : null;
  } catch {
    return null;
  }
}

/** Clear the stored return path. Call once the post-login navigation has fired. */
export function clearStoredRedirect(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve the post-login destination from every supported source, in priority
 * order: explicit `?redirect=` → legacy `?returnUrl=` → stored value → fallback.
 * Does NOT mutate storage — call {@link clearStoredRedirect} after navigating.
 */
export function resolveRedirectTarget(
  params: URLSearchParams | null | undefined,
  fallback = '/me/dashboard',
): string {
  const fromParam = params?.get(REDIRECT_PARAM);
  if (isSafeRedirect(fromParam)) return fromParam;
  const fromLegacy = params?.get(LEGACY_REDIRECT_PARAM);
  if (isSafeRedirect(fromLegacy)) return fromLegacy;
  const fromStore = peekStoredRedirect();
  if (fromStore) return fromStore;
  return fallback;
}
